import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { RoleGate } from "@/components/gate/RoleGate";
import { GroupedTable } from "@/components/GroupedTable/GroupedTable";
import { Button } from "@/components/ui/button";
import { Column, Row } from "@/components/ui/flex";
import { Switch } from "@/components/ui/switch";
import { useExternalConnectionsData } from "@/data/use-external-connections-data";
import { useUserGroupsData } from "@/data/use-groups-data";
import { useDebugSettings } from "@/hooks/use-debug-settings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@nnr/fontawesome-sharp-light/icons/sharp/light";

export default function UserSettingsPage() {
  const { groups } = useUserGroupsData();
  const { overlay: overlaySettings, setOverlay } = useDebugSettings();
  const { connections, deleteConnection } = useExternalConnectionsData(null);
  const activeConnections = connections.filter((c) => c.status === "active");

  const handleConnectionDelete = (connectionUuid: string) => {
    deleteConnection(connectionUuid);
  };

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>User Settings</PageHeader.Title>
      </PageHeader>
      <PageBody>
        <Column className="gap-2 space-y-10">
          <section id="connections" className="space-y-4">
            <h1 className="text-2xl font-bold">Connections</h1>
            <div>
              Manage your connections to external providers on each group.
            </div>
            <GroupedTable
              data={[
                {
                  groupId: "connections",
                  label: "Connections",
                  items: activeConnections,
                },
              ]}
              columns={[
                { key: "provider", label: "Provider" },
                { key: "group_uuid", label: "Group" },
              ]}
              itemIdKey="uuid"
              renderCell={{
                group_uuid: (groupUuid) => {
                  return groups.find((ws) => ws.uuid === groupUuid)?.name || "";
                },
              }}
              renderActions={({ row: connection }) => (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => handleConnectionDelete(connection.uuid)}
                  className="hover:bg-destructive hover:text-destructive-foreground"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              )}
              emptyMessage="No active connections. Please connect an external source first."
            />
          </section>
          <RoleGate systemRoles="super">
            <section id="overlays" className="space-y-4">
              <h1 className="text-2xl font-bold">Overlays</h1>

              <Row className="items-center gap-2">
                <Switch
                  checked={overlaySettings}
                  onCheckedChange={(value) => setOverlay(value)}
                />
                <span>Enable Overlays</span>
              </Row>

              <h2 className="text-lg font-semibold">Preview</h2>
              <Row className="gap-2">
                <div>Only super admins can see this:</div>
                <RoleGate systemRoles="super">
                  <p>I CAN SEE!</p>
                </RoleGate>
              </Row>
              <Row className="gap-2">
                <div>Only non super admins can see this:</div>
                <RoleGate systemRoles={["user"]}>
                  <p>I CAN SEE NOW!</p>
                </RoleGate>
              </Row>
            </section>
          </RoleGate>
        </Column>
      </PageBody>
    </Page>
  );
}
